import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getAiInsights, AiInsights, AiInsightsSession } from '../utils/api';
import { useSEO } from '../hooks/useSEO';

const AiInsightsDashboardPage: React.FC = () => {
  useSEO({
    title: 'AI Insights — Retrosive',
    description: 'Cross-sprint AI insights for your team: recurring issues, improving areas, unresolved action items, and sentiment trends.',
    robots: 'noindex, nofollow',
  });

  const { tokens } = useAuthStore();
  const [insights, setInsights] = useState<AiInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tokens?.access) return;
    setLoading(true);
    getAiInsights(tokens.access)
      .then(setInsights)
      .catch(() => setError('Could not load insights. Run more AI reports to see patterns.'))
      .finally(() => setLoading(false));
  }, [tokens?.access]);

  const healthColor = (score: number | null) => {
    if (score === null) return 'text-slate-400';
    if (score >= 70) return 'text-emerald-500';
    if (score >= 40) return 'text-amber-500';
    return 'text-red-500';
  };

  const sentimentEmoji = (label: string) => {
    if (!label) return '😐';
    const l = label.toLowerCase();
    if (l.includes('positive') || l.includes('good')) return '😊';
    if (l.includes('negative') || l.includes('bad')) return '😔';
    return '😐';
  };

  const sentimentColor = (label: string) => {
    if (!label) return 'text-slate-400';
    const l = label.toLowerCase();
    if (l.includes('positive') || l.includes('good')) return 'text-emerald-600';
    if (l.includes('negative') || l.includes('bad')) return 'text-red-500';
    return 'text-amber-500';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">✨</span>
            <h1 className="text-2xl font-bold text-slate-900">AI Insights Dashboard</h1>
          </div>
          <p className="text-slate-500">
            Cross-sprint patterns from your last {insights?.total_sessions ?? '—'} AI retrospective sessions.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <div className="text-4xl mb-4">🤖</div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">No insights yet</h2>
            <p className="text-slate-500 mb-6 max-w-sm mx-auto">{error}</p>
            <Link
              to="/app/dashboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors"
            >
              Go to your boards
            </Link>
          </div>
        )}

        {!loading && insights && insights.total_sessions === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <div className="text-4xl mb-4">🚀</div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Run your first AI report</h2>
            <p className="text-slate-500 mb-6 max-w-sm mx-auto">
              After 3+ AI retrospective reports, Retrosive will surface patterns, recurring issues, and team health trends here.
            </p>
            <Link
              to="/app/dashboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors"
            >
              Open a board to generate AI report
            </Link>
          </div>
        )}

        {!loading && insights && insights.total_sessions > 0 && (
          <div className="space-y-6">

            {/* Health Score */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Team Health Score</p>
                <p className={`text-5xl font-black ${healthColor(insights.health_score)}`}>
                  {insights.health_score ?? '—'}
                </p>
                <p className="text-xs text-slate-400 mt-1">out of 100</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Sessions Analysed</p>
                <p className="text-5xl font-black text-slate-700">{insights.total_sessions}</p>
                <p className="text-xs text-slate-400 mt-1">AI retrospective reports</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Recurring Issues</p>
                <p className="text-5xl font-black text-red-500">{insights.recurring_issues.length}</p>
                <p className="text-xs text-slate-400 mt-1">topics stuck in the cycle</p>
              </div>
            </div>

            {/* Recurring Issues + Improving Areas */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-lg">🔴</span>
                  <h2 className="font-bold text-slate-900">Recurring Issues</h2>
                </div>
                {insights.recurring_issues.length > 0 ? (
                  <ul className="space-y-2">
                    {insights.recurring_issues.map((issue) => (
                      <li key={issue} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full bg-red-400" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400">No recurring issues detected yet.</p>
                )}
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-lg">📈</span>
                  <h2 className="font-bold text-slate-900">Improving Areas</h2>
                </div>
                {insights.improving_areas.length > 0 ? (
                  <ul className="space-y-2">
                    {insights.improving_areas.map((area) => (
                      <li key={area} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {area}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400">No clear improving areas detected yet.</p>
                )}
              </div>
            </div>

            {/* Unresolved Action Items */}
            {insights.unresolved_action_items.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-lg">⚡</span>
                  <h2 className="font-bold text-slate-900">
                    Action Items ({insights.unresolved_action_items.length})
                  </h2>
                </div>
                <ul className="space-y-2">
                  {insights.unresolved_action_items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sentiment Timeline */}
            {insights.sentiment_trend.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-lg">💬</span>
                  <h2 className="font-bold text-slate-900">Team Sentiment Trend</h2>
                </div>
                <div className="flex items-end gap-3 h-24">
                  {insights.sentiment_trend.map((point, i) => {
                    const pct = Math.round(point.score * 100);
                    const barH = Math.max(8, pct);
                    return (
                      <div key={i} className="flex flex-col items-center gap-1 flex-1">
                        <span className="text-xs text-slate-400">{sentimentEmoji(point.label)}</span>
                        <div
                          className="w-full rounded-t-lg bg-orange-400"
                          style={{ height: `${barH}%` }}
                          title={`${point.date}: ${pct}% (${point.label})`}
                        />
                        <span className="text-[10px] text-slate-400 rotate-45 origin-left mt-1 whitespace-nowrap">
                          {point.date.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Analysed Sessions */}
            {insights.sessions.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-lg">📋</span>
                  <h2 className="font-bold text-slate-900">Analysed Sessions</h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {insights.sessions.map((session: AiInsightsSession, i: number) => (
                    <div key={i} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0 text-sm font-bold text-orange-500">
                          {i + 1}
                        </div>
                        <div className="min-w-0">
                          {session.board_id ? (
                            <Link
                              to={`/app/board/${session.board_id}`}
                              className="text-sm font-medium text-slate-800 hover:text-orange-500 transition-colors truncate block"
                            >
                              {session.board_title}
                            </Link>
                          ) : (
                            <span className="text-sm font-medium text-slate-800 truncate block">
                              {session.board_title}
                            </span>
                          )}
                          <span className="text-xs text-slate-400">{session.date}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-4">
                        <span className="text-base">{sentimentEmoji(session.sentiment_label)}</span>
                        <span className={`text-xs font-medium capitalize ${sentimentColor(session.sentiment_label)}`}>
                          {session.sentiment_label || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

export default AiInsightsDashboardPage;
